import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/Input';

import { WIKI_FACT_KIND_LABELS, type WikiFactKind } from '../people-wiki.types';

import { useAddFactMutation } from './edits.query';

/** A row to type in one fact: its kind, then the text. The saved fact is listed with the others of its kind. */
export const AddFactForm = ({
  personId,
  onDone,
}: {
  personId: string;
  onDone: () => void;
}) => {
  const [kind, setKind] = useState<WikiFactKind>('other');
  const [text, setText] = useState('');
  const { mutate: addFact, isPending } = useAddFactMutation(personId);
  const trimmedText = text.trim();

  return (
    <form
      className="flex items-center gap-1.5 self-stretch"
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmedText !== '') {
          addFact({ kind, text: trimmedText }, { onSuccess: onDone });
        }
      }}
    >
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          aria-label="Kind of fact"
          className="h-8 shrink-0 text-xs"
          disabled={isPending}
        >
          <span>{WIKI_FACT_KIND_LABELS[kind]}</span>
          <Icons.chevronDown className="size-3 shrink-0" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="start">
          <DropdownMenu.RadioGroup
            value={kind}
            onValueChange={(next) => setKind(next as WikiFactKind)}
          >
            {Object.entries(WIKI_FACT_KIND_LABELS).map(([value, label]) => (
              <DropdownMenu.RadioItem key={value} value={value}>
                {label}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      <Input
        inputSize="sm"
        aria-label="Fact"
        placeholder="Something worth knowing about this person"
        value={text}
        maxLength={2000}
        disabled={isPending}
        autoFocus
        onChange={(event) => setText(event.target.value)}
      />
      <Button
        type="submit"
        size="xs"
        isLoading={isPending}
        disabled={trimmedText === ''}
      >
        Add
      </Button>
      <Button size="xs" variant="ghost" disabled={isPending} onClick={onDone}>
        Cancel
      </Button>
    </form>
  );
};
